/**
 * Volume Role - Validate Pillar
 *
 * Validação semântica de valores de volume.
 * Zero dependências externas - pode ser usado standalone.
 *
 * @example
 * import { validateVolume, isValidVolume } from '@attrx/role-morphic/volume/validate';
 *
 * validateVolume(100);   // { valid: true, errors: [] }
 * validateVolume(-5);    // { valid: false, errors: ['Volume cannot be negative'] }
 * isValidVolume(100);    // true
 */

// =============================================================================
// TYPES
// =============================================================================

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

export interface VolumeValidationConfig {
  min?: number;
  max?: number;
  integer?: boolean;
  minError?: string;
  maxError?: string;
}

// =============================================================================
// CONFIG
// =============================================================================

/**
 * Configuração padrão de validação para volume
 */
export const VOLUME_VALIDATION_CONFIG: VolumeValidationConfig = {
  min: 0,
  minError: "Volume cannot be negative",
};

// =============================================================================
// VALIDATE
// =============================================================================

/**
 * Valida um valor de volume
 *
 * @param value - Valor a ser validado
 * @param config - Configuração customizada (opcional)
 * @returns Resultado da validação com erros se houver
 *
 * @example
 * validateVolume(100);              // { valid: true, errors: [] }
 * validateVolume(-5);               // { valid: false, errors: ['Volume cannot be negative'] }
 * validateVolume(150, { max: 100 }) // { valid: false, errors: ['Value must be at most 100'] }
 */
export function validateVolume(
  value: unknown,
  config: VolumeValidationConfig = VOLUME_VALIDATION_CONFIG
): ValidationResult {
  const errors: string[] = [];

  // Type check
  if (typeof value !== "number") {
    return { valid: false, errors: ["Value must be a number"] };
  }

  // Finite check
  if (!Number.isFinite(value)) {
    return { valid: false, errors: ["Value must be finite"] };
  }

  // Min check
  if (config.min !== undefined && value < config.min) {
    const msg = config.minError ?? `Value must be at least ${config.min}`;
    errors.push(msg);
  }

  // Max check
  if (config.max !== undefined && value > config.max) {
    const msg = config.maxError ?? `Value must be at most ${config.max}`;
    errors.push(msg);
  }

  // Integer check
  if (config.integer && !Number.isInteger(value)) {
    errors.push("Value must be an integer");
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Verifica se um valor de volume é válido
 *
 * @param value - Valor a ser validado
 * @param config - Configuração customizada (opcional)
 * @returns true se válido, false caso contrário
 *
 * @example
 * isValidVolume(100);  // true
 * isValidVolume(-5);   // false
 */
export function isValidVolume(
  value: unknown,
  config: VolumeValidationConfig = VOLUME_VALIDATION_CONFIG
): boolean {
  return validateVolume(value, config).valid;
}
